import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  DataTable,
  EmptyState,
  FilterBar,
  FilterChips,
  IconButton,
  Pagination,
  Pill,
  SelectField,
  formatCpf,
  type Column,
} from "@atlas/ui/web";
import { atlas } from "../../../../lib/sdk";
import type { BancoPerfil, BancoUsuario } from "@atlas/sdk";
import { POR_PAGINA_PADRAO, recorteDoServidor } from "../../../../lib/recorte";
import { useDebouncedValue } from "../../../../lib/use-debounce";
import { useFiltros } from "../../../../lib/use-filtros";

function CopyCpfButton({ usuarioId }: { usuarioId: string }) {
  const [state, setState] = useState<"idle" | "loading" | "ok" | "err">("idle");
  async function copy() {
    if (state === "loading") return;
    setState("loading");
    try {
      const { cpf } = await atlas.banco.revealUsuarioCpf(usuarioId);
      await navigator.clipboard.writeText(formatCpf(cpf));
      setState("ok");
    } catch {
      setState("err");
    } finally {
      setTimeout(() => setState("idle"), 1800);
    }
  }
  const title = state === "ok" ? "CPF copiado!" : state === "err" ? "Falha ao copiar" : "Copiar CPF";
  const glyph = state === "ok" ? "✓" : state === "err" ? "!" : state === "loading" ? "…" : "⧉";
  return (
    <IconButton title={title} onClick={copy} danger={state === "err"}>
      {glyph}
    </IconButton>
  );
}

const PERFIS: { value: BancoPerfil | ""; label: string }[] = [
  { value: "", label: "Todos os perfis" },
  { value: "admin", label: "Admin" },
  { value: "operador", label: "Operador" },
  { value: "consulta", label: "Consulta" },
  { value: "consulta_margem", label: "Consulta de margem" },
  { value: "relatorios", label: "Relatórios" },
  { value: "operadora", label: "Benefícios — operador" },
  { value: "operadora_consulta", label: "Benefícios — consulta" },
  { value: "personalizado", label: "Personalizado" },
];

export function BancoUsuariosLista() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [perfil, setPerfil] = useState<BancoPerfil | "">("");
  const [search, setSearch] = useState("");
  // SITUACAO entrou e `somenteAdmin` saiu, no mesmo passo.
  //
  // A coluna Situação existia na grade e nao era recortavel — administracao de
  // acesso e' exatamente a tarefa que depende desse estado. E `somenteAdmin`
  // era `perfil = "admin"` com outro nome: dois caminhos para o mesmo recorte,
  // e marcado junto com outro perfil devolvia lista VAZIA sem erro, porque
  // ninguem tem dois perfis.
  const [situacao, setSituacao] = useState<"" | "ativo" | "inativo">("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);

  // O input guarda o valor CRU (a digitacao tem que aparecer na hora) e a
  // consulta usa o atrasado. Sem isto cada tecla e uma queryKey nova, ou seja
  // um request: a tela ficaria PIOR que a versao que filtrava em memoria.
  const buscaDebounced = useDebouncedValue(search);

  const data = useQuery({
    queryKey: ["banco", "usuarios", perfil, situacao, buscaDebounced, pagina, porPagina],
    queryFn: () => atlas.banco.listUsuarios({
      perfil: perfil || undefined,
      situacao: situacao || undefined,
      q: buscaDebounced || undefined,
      limit: porPagina,
      offset: (pagina - 1) * porPagina,
    }),
    // Mantem a pagina anterior na tela enquanto a nova carrega, em vez de
    // piscar a tabela vazia a cada virada.
    placeholderData: (anterior) => anterior,
  });

  /** Trocar filtro com a posicao antiga cairia num offset que o novo recorte
   *  pode nem ter. (Trocar o TAMANHO da pagina nao precisa disto: o
   *  `Pagination` ja chama `onPagina(1)` por dentro.) */
  const filtros = useFiltros(
    [
      { nome: "busca", ligado: search !== "", limpar: () => setSearch("") },
      { nome: "situacao", ligado: situacao !== "", limpar: () => setSituacao("") },
      { nome: "perfil", ligado: perfil !== "", limpar: () => setPerfil("") },
    ],
    setPagina,
  );
  const { filtrar } = filtros;

  const remove = useMutation({
    mutationFn: (id: string) => atlas.banco.removerUsuario(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["banco", "usuarios"] });
      qc.invalidateQueries({ queryKey: ["banco", "usuario", id] });
    },
  });
  const reactivate = useMutation({
    mutationFn: (id: string) => atlas.banco.reativarUsuario(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["banco", "usuarios"] });
      qc.invalidateQueries({ queryKey: ["banco", "usuario", id] });
    },
  });

  // A pagina JA vem fatiada do Postgres, com a busca e a ordem aplicadas la —
  // `ORDER BY criado_em DESC, id DESC` sobre o timestamp real, nao sobre a
  // string de data. `recorteDoServidor` da a mesma forma que `recortar` daria,
  // que e o que deixa a diferenca entre cliente e servidor invisivel na tela.
  //
  // Filtrar aqui de novo seria o defeito inverso: recortaria so a pagina
  // corrente, e o operador veria apenas os que CALHARAM de estar nas 25 linhas.
  const daPagina = recorteDoServidor(
    data.data?.usuarios ?? [],
    data.data?.total ?? 0,
    pagina,
    porPagina,
  );

  const columns: Column<BancoUsuario>[] = [
    { key: "ativo", header: "Situação", render: (u) => <Pill variant={u.ativo ? "averbado" : "expirado"}>{u.ativo ? "Ativo" : "Inativo"}</Pill> },
    { key: "codigo", header: "Código", mono: true },
    { key: "nome", header: "Nome" },
    { key: "email", header: "Login" },
    { key: "cpfMasked", header: "CPF", mono: true },
    {
      key: "temSenha",
      header: "Acesso",
      // Um operador sem senha aparece na grid igual a qualquer outro e nao
      // consegue entrar. Sem esta coluna, a unica forma de descobrir isso era
      // o proprio operador reclamar que o login nao funciona.
      render: (u) =>
        u.temSenha ? (
          <Pill variant={u.senhaTemporaria ? "pendente" : "averbado"}>
            {u.senhaTemporaria ? "Senha temporária" : "Liberado"}
          </Pill>
        ) : (
          <Pill variant="expirado">Sem senha</Pill>
        ),
    },
    { key: "perfil", header: "Perfil" },
    {
      key: "permissoes", header: "Permissões",
      render: (u) => (
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {u.permissoes?.includes("*") ? "todas (*)" : `${u.permissoes?.length ?? 0} marcadas`}
        </span>
      ),
    },
    { key: "organizacao", header: "Organização" },
    { key: "subOrganizacao", header: "Sub organização", render: (u) => u.subOrganizacaoNome ?? "—" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Cadastros
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Operadores</h1>
          <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--text-muted)", maxWidth: 640 }}>
            Sub-usuários do time da consignatária que acessam este painel (analistas, gerentes, atendentes).
            Servidores/clientes que têm contrato com esta consignatária aparecem em <b>Meus Contratos</b> e <b>Bate de Carteira</b>.
          </p>
        </div>
        <Button onClick={() => nav("novo")}>+ Inserir</Button>
      </header>

      <FilterBar
        layout="linha"
        searchValue={search}
        onSearchChange={filtrar(setSearch)}
        searchPlaceholder="Buscar por nome, login, CPF ou código"
        {...filtros.propsFilterBar}
        actions={
          <FilterChips
            variant="segmentado"
            label="Situação"
            value={situacao}
            onChange={(v) => filtrar(setSituacao)(v as "" | "ativo" | "inativo")}
            options={[
              { value: "", label: "Todos" },
              { value: "ativo", label: "Ativos" },
              { value: "inativo", label: "Inativos" },
            ]}
          />
        }
      >
        {/* Perfil segue como select de escolha unica: sao cinco valores fixos e
            a comparacao entre dois nao e' a tarefa. O checkbox "apenas
            administradores" saiu — era este mesmo select com valor `admin`. */}
        <div style={{ minWidth: 220 }}>
          <SelectField label="Perfil" value={perfil} onChange={(e) => filtrar(setPerfil)(e.target.value as BancoPerfil | "")} options={PERFIS.map((p) => ({ value: p.value, label: p.label }))} />
        </div>
      </FilterBar>

      <DataTable
        columns={columns}
        rows={daPagina.itens}
        rowKey={(u) => u.id}
        loading={data.isLoading}
        emptyState={
          data.isError
            ? "Não foi possível carregar os operadores."
            : <EmptyState
                // Sem `totalBase` da rota, a decisao sai de `temFiltro` — e' o
                // que esta tela tem. Fica correto nos dois casos que importam:
                // base vazia sem filtro, e recorte vazio com filtro.
                motivo={filtros.temFiltro ? "filtro-vazio" : "sem-dados"}
                filtroVazio="Nenhum operador bate com os filtros atuais."
                semDados="Nenhum operador cadastrado. Clique em '+ Inserir' para criar o primeiro."
                onLimpar={filtros.limparFiltros}
              />
        }
        actions={(u) => (
          <>
            <IconButton title="Editar" onClick={() => nav(u.id)}>✎</IconButton>
            <CopyCpfButton usuarioId={u.id} />
            {u.ativo ? (
              <IconButton
                title="Desativar"
                danger
                onClick={() => {
                  if (confirm(`Desativar ${u.nome}?\n\nO usuário para de operar, mas nada é apagado — você pode reativar depois.`)) remove.mutate(u.id);
                }}
              >
                ⏸
              </IconButton>
            ) : (
              <IconButton title="Reativar" onClick={() => reactivate.mutate(u.id)}>▶</IconButton>
            )}
          </>
        )}
      />
      <Pagination
        total={daPagina.total}
        pagina={daPagina.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="operadores"
      />
    </div>
  );
}
